<template>
  <div class="modal fade" id="providerConfigModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <img v-if="provider.logo" :src="provider.logo" :alt="provider.name" style="width: 24px; height: 24px;" class="me-2">
            Configure {{ provider.name }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <div class="row">
            <!-- General Configuration -->
            <div class="col-12 mb-4">
              <div class="card border">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="mdi mdi-cog me-2"></i>
                    General Configuration
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Provider Status</label>
                        <select v-model="config.status" class="form-select">
                          <option value="ACTIVE">Active</option>
                          <option value="INACTIVE">Inactive</option>
                          <option value="MAINTENANCE">Maintenance</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Priority Level</label>
                        <select v-model="config.priority" class="form-select">
                          <option value="HIGH">High</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="LOW">Low</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Timeout (seconds)</label>
                        <input v-model="config.timeout" type="number" class="form-control" min="5" max="300">
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Retry Attempts</label>
                        <input v-model="config.retryAttempts" type="number" class="form-control" min="1" max="5">
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Daily Transaction Limit</label>
                        <input v-model="config.dailyLimit" type="number" class="form-control" step="1000">
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Single Transaction Limit</label>
                        <input v-model="config.transactionLimit" type="number" class="form-control" step="100">
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- API Configuration -->
            <div class="col-12 mb-4">
              <div class="card border">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="mdi mdi-api me-2"></i>
                    API Configuration
                  </h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <label class="form-label">API Endpoint URL</label>
                    <input v-model="config.apiUrl" type="url" class="form-control" placeholder="https://api.provider.com/v1/">
                  </div>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">API Key</label>
                        <div class="input-group">
                          <input v-model="config.apiKey" :type="showApiKey ? 'text' : 'password'" class="form-control">
                          <button @click="showApiKey = !showApiKey" class="btn btn-outline-secondary" type="button">
                            <i class="mdi" :class="showApiKey ? 'mdi-eye-off' : 'mdi-eye'"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">API Secret</label>
                        <div class="input-group">
                          <input v-model="config.apiSecret" :type="showApiSecret ? 'text' : 'password'" class="form-control">
                          <button @click="showApiSecret = !showApiSecret" class="btn btn-outline-secondary" type="button">
                            <i class="mdi" :class="showApiSecret ? 'mdi-eye-off' : 'mdi-eye'"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Organization Code</label>
                        <input v-model="config.orgCode" type="text" class="form-control">
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Short Code</label>
                        <input v-model="config.shortCode" type="text" class="form-control">
                      </div>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label">Callback URL</label>
                    <input v-model="config.callbackUrl" type="url" class="form-control" placeholder="https://your-domain.com/callback">
                  </div>

                  <div class="form-check">
                    <input v-model="config.enableSsl" class="form-check-input" type="checkbox" id="enableSsl">
                    <label class="form-check-label" for="enableSsl">
                      Enable SSL Certificate Verification
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Transaction Configuration -->
            <div class="col-12 mb-4">
              <div class="card border">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="mdi mdi-cash-multiple me-2"></i>
                    Transaction Configuration
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="mb-3">
                        <label class="form-label">Minimum Amount (KES)</label>
                        <input v-model="config.minAmount" type="number" class="form-control" min="1">
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="mb-3">
                        <label class="form-label">Maximum Amount (KES)</label>
                        <input v-model="config.maxAmount" type="number" class="form-control">
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="mb-3">
                        <label class="form-label">Transaction Fee (%)</label>
                        <input v-model="config.transactionFee" type="number" step="0.01" class="form-control" min="0" max="10">
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Supported Currencies</label>
                        <select v-model="config.supportedCurrencies" class="form-select" multiple>
                          <option value="KES">Kenyan Shilling (KES)</option>
                          <option value="USD">US Dollar (USD)</option>
                          <option value="EUR">Euro (EUR)</option>
                          <option value="GBP">British Pound (GBP)</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Settlement Period</label>
                        <select v-model="config.settlementPeriod" class="form-select">
                          <option value="INSTANT">Instant</option>
                          <option value="T+0">Same Day (T+0)</option>
                          <option value="T+1">Next Day (T+1)</option>
                          <option value="T+2">T+2</option>
                          <option value="T+3">T+3</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-check">
                        <input v-model="config.enableRefunds" class="form-check-input" type="checkbox" id="enableRefunds">
                        <label class="form-check-label" for="enableRefunds">
                          Enable Refunds
                        </label>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-check">
                        <input v-model="config.enablePartialRefunds" class="form-check-input" type="checkbox" id="enablePartialRefunds">
                        <label class="form-check-label" for="enablePartialRefunds">
                          Enable Partial Refunds
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Security Configuration -->
            <div class="col-12 mb-4">
              <div class="card border">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="mdi mdi-shield-check me-2"></i>
                    Security Configuration
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Encryption Method</label>
                        <select v-model="config.encryptionMethod" class="form-select">
                          <option value="AES-256">AES-256</option>
                          <option value="RSA-2048">RSA-2048</option>
                          <option value="SHA-256">SHA-256</option>
                        </select>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">IP Whitelist</label>
                        <textarea v-model="config.ipWhitelist" class="form-control" rows="3"
                                  placeholder="Enter IP addresses separated by commas"></textarea>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-check">
                        <input v-model="config.requireOtp" class="form-check-input" type="checkbox" id="requireOtp">
                        <label class="form-check-label" for="requireOtp">
                          Require OTP for Transactions
                        </label>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-check">
                        <input v-model="config.enableFraudDetection" class="form-check-input" type="checkbox" id="enableFraudDetection">
                        <label class="form-check-label" for="enableFraudDetection">
                          Enable Fraud Detection
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Monitoring & Alerts -->
            <div class="col-12 mb-4">
              <div class="card border">
                <div class="card-header bg-light">
                  <h6 class="mb-0">
                    <i class="mdi mdi-monitor-dashboard me-2"></i>
                    Monitoring & Alerts
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Health Check Interval (minutes)</label>
                        <input v-model="config.healthCheckInterval" type="number" class="form-control" min="1" max="60">
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Alert Threshold (%)</label>
                        <input v-model="config.alertThreshold" type="number" class="form-control" min="80" max="100" step="0.1">
                      </div>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label">Alert Recipients (Email)</label>
                    <textarea v-model="config.alertRecipients" class="form-control" rows="2"
                              placeholder="Enter email addresses separated by commas"></textarea>
                  </div>

                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-check">
                        <input v-model="config.enableDowntimeAlerts" class="form-check-input" type="checkbox" id="enableDowntimeAlerts">
                        <label class="form-check-label" for="enableDowntimeAlerts">
                          Enable Downtime Alerts
                        </label>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-check">
                        <input v-model="config.enablePerformanceAlerts" class="form-check-input" type="checkbox" id="enablePerformanceAlerts">
                        <label class="form-check-label" for="enablePerformanceAlerts">
                          Enable Performance Alerts
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Test Configuration -->
            <div class="col-12">
              <div class="card border border-info">
                <div class="card-header bg-info text-white">
                  <h6 class="mb-0 text-white">
                    <i class="mdi mdi-test-tube me-2"></i>
                    Test Configuration
                  </h6>
                </div>
                <div class="card-body">
                  <div class="alert alert-info">
                    <i class="mdi mdi-information me-2"></i>
                    Test the provider configuration before saving to ensure connectivity and proper setup.
                  </div>

                  <div class="d-flex gap-2 mb-3">
                    <button @click="testConnection" :disabled="testing.connection" class="btn btn-outline-primary">
                      <i v-if="!testing.connection" class="mdi mdi-lan-connect me-1"></i>
                      <div v-else class="spinner-border spinner-border-sm me-1" role="status"></div>
                      Test Connection
                    </button>

                    <button @click="testTransaction" :disabled="testing.transaction" class="btn btn-outline-success">
                      <i v-if="!testing.transaction" class="mdi mdi-cash me-1"></i>
                      <div v-else class="spinner-border spinner-border-sm me-1" role="status"></div>
                      Test Transaction
                    </button>

                    <button @click="testCallback" :disabled="testing.callback" class="btn btn-outline-warning">
                      <i v-if="!testing.callback" class="mdi mdi-webhook me-1"></i>
                      <div v-else class="spinner-border spinner-border-sm me-1" role="status"></div>
                      Test Callback
                    </button>
                  </div>

                  <div v-if="testResults.length > 0" class="border rounded p-3 bg-light">
                    <h6>Test Results:</h6>
                    <div v-for="result in testResults" :key="result.test" class="d-flex justify-content-between align-items-center mb-2">
                      <span>{{ result.test }}</span>
                      <span class="badge" :class="result.success ? 'bg-success' : 'bg-danger'">
                        {{ result.success ? 'PASSED' : 'FAILED' }}
                      </span>
                    </div>
                    <small class="text-muted">{{ testResults[testResults.length - 1].message }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button @click="resetToDefaults" class="btn btn-outline-secondary">
                <i class="mdi mdi-backup-restore me-1"></i>
                Reset to Defaults
              </button>
            </div>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                Cancel
              </button>
              <button @click="saveConfiguration" :disabled="saving" class="btn btn-primary">
                <i v-if="!saving" class="mdi mdi-content-save me-1"></i>
                <div v-else class="spinner-border spinner-border-sm me-1" role="status"></div>
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: "ProviderConfigModal",
  props: {
    provider: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      showApiKey: false,
      showApiSecret: false,
      saving: false,

      testing: {
        connection: false,
        transaction: false,
        callback: false
      },

      testResults: [],

      config: {
        // General
        status: 'ACTIVE',
        priority: 'HIGH',
        timeout: 30,
        retryAttempts: 3,
        dailyLimit: 10000000,
        transactionLimit: 500000,

        // API
        apiUrl: '',
        apiKey: '',
        apiSecret: '',
        orgCode: '',
        shortCode: '',
        callbackUrl: '',
        enableSsl: true,

        // Transaction
        minAmount: 1,
        maxAmount: 500000,
        transactionFee: 0,
        supportedCurrencies: ['KES'],
        settlementPeriod: 'T+1',
        enableRefunds: true,
        enablePartialRefunds: false,

        // Security
        encryptionMethod: 'AES-256',
        ipWhitelist: '',
        requireOtp: false,
        enableFraudDetection: true,

        // Monitoring
        healthCheckInterval: 5,
        alertThreshold: 95,
        alertRecipients: '',
        enableDowntimeAlerts: true,
        enablePerformanceAlerts: true
      }
    }
  },
  watch: {
    provider: {
      handler(newProvider) {
        if (newProvider && newProvider.id) {
          this.loadProviderConfig(newProvider);
        }
      },
      immediate: true
    }
  },
  methods: {
    loadProviderConfig(provider) {
      // Load existing configuration or set defaults based on provider type
      const defaults = this.getProviderDefaults(provider);
      this.config = { ...this.config, ...defaults };
      this.testResults = [];
    },

    getProviderDefaults(provider) {
      const defaults = {
        'MPESA': {
          apiUrl: 'https://sandbox.safaricom.co.ke/mpesa/',
          shortCode: '174379',
          orgCode: 'COUNTY001',
          minAmount: 1,
          maxAmount: 70000,
          transactionFee: 0,
          settlementPeriod: 'INSTANT',
          callbackUrl: 'https://county.go.ke/api/mpesa/callback'
        },
        'AIRTEL': {
          apiUrl: 'https://api.airtel.africa/v1/',
          shortCode: 'AIRTEL001',
          orgCode: 'COUNTY001',
          minAmount: 5,
          maxAmount: 500000,
          transactionFee: 0,
          settlementPeriod: 'T+1',
          callbackUrl: 'https://county.go.ke/api/airtel/callback'
        },
        'EQUITEL': {
          apiUrl: 'https://api.equitel.com/v1/',
          shortCode: 'EQUITEL001',
          orgCode: 'COUNTY001',
          minAmount: 10,
          maxAmount: 300000,
          transactionFee: 0,
          settlementPeriod: 'T+1',
          callbackUrl: 'https://county.go.ke/api/equitel/callback'
        },
        'TKASH': {
          apiUrl: 'https://api.telkom.co.ke/v1/',
          shortCode: 'TKASH001',
          orgCode: 'COUNTY001',
          minAmount: 5,
          maxAmount: 150000,
          transactionFee: 0,
          settlementPeriod: 'T+1',
          callbackUrl: 'https://county.go.ke/api/tkash/callback'
        }
      };

      return defaults[provider.code] || {};
    },

    async testConnection() {
      this.testing.connection = true;

      try {
        // Simulate API test with provider-specific logic
        await new Promise(resolve => setTimeout(resolve, 2000));

        const success = Math.random() > 0.1; // 90% success rate

        this.testResults.push({
          test: 'Connection Test',
          success: success,
          message: success
              ? `Successfully connected to ${this.provider.name} API endpoint`
              : `Failed to connect to ${this.provider.name} API - Check credentials and network`
        });
      } catch (error) {
        this.testResults.push({
          test: 'Connection Test',
          success: false,
          message: 'Connection test failed: ' + error.message
        });
      } finally {
        this.testing.connection = false;
      }
    },

    async testTransaction() {
      this.testing.transaction = true;

      try {
        // Simulate transaction test with realistic scenarios
        await new Promise(resolve => setTimeout(resolve, 3000));

        const success = Math.random() > 0.15; // 85% success rate
        const testAmount = this.config.minAmount || 10;

        this.testResults.push({
          test: 'Transaction Test',
          success: success,
          message: success
              ? `Test transaction of KES ${testAmount} completed successfully via ${this.provider.name}`
              : `Test transaction failed - Verify API credentials and transaction limits`
        });
      } catch (error) {
        this.testResults.push({
          test: 'Transaction Test',
          success: false,
          message: 'Transaction test failed: ' + error.message
        });
      } finally {
        this.testing.transaction = false;
      }
    },

    async testCallback() {
      this.testing.callback = true;

      try {
        // Simulate callback URL test
        await new Promise(resolve => setTimeout(resolve, 1500));

        const success = this.config.callbackUrl && this.config.callbackUrl.startsWith('https://');

        this.testResults.push({
          test: 'Callback Test',
          success: success,
          message: success
              ? `Callback URL ${this.config.callbackUrl} is reachable and responding correctly`
              : 'Callback URL test failed - Ensure URL is valid and accessible'
        });
      } catch (error) {
        this.testResults.push({
          test: 'Callback Test',
          success: false,
          message: 'Callback test failed: ' + error.message
        });
      } finally {
        this.testing.callback = false;
      }
    },

    resetToDefaults() {
      const defaults = this.getProviderDefaults(this.provider);
      this.config = { ...this.config, ...defaults };
      this.testResults = [];
    },

    async saveConfiguration() {
      this.saving = true;

      try {
        // Validate required fields
        const requiredFields = ['apiUrl', 'apiKey', 'shortCode'];
        const missingFields = requiredFields.filter(field => !this.config[field]);

        if (missingFields.length > 0) {
          throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }

        // Simulate API save
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Emit the configuration to parent
        this.$emit('save-configuration', {
          name: this.provider.name,
          code: this.provider.code,
          config: this.config
        });

        // Close modal
        const modalElement = document.getElementById('providerConfigModal');
        if (modalElement) {
          const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
          if (bootstrapModal) {
            bootstrapModal.hide();
          }
        }
      } catch (error) {
        alert('Configuration save failed: ' + error.message);
      } finally {
        this.saving = false;
      }
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 600;
  font-size: 0.875rem;
}

.card-header {
  padding: 0.75rem 1rem;
}

.card-body {
  padding: 1rem;
}

.badge {
  font-size: 0.75rem;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.alert {
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.border {
  border-color: #dee2e6 !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.modal-lg {
  max-width: 900px;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.input-group .btn {
  border-left: none;
}

.form-select[multiple] {
  height: auto;
}

textarea.form-control {
  resize: vertical;
}
</style>